import {
  GutterOversize,
  Wall as ConfigWall,
  WallInsertObject,
} from "src/types/configurator";
import {
  Configuration,
  Coordinate,
  Position,
  Wall,
} from "src/types/configuration";
import { getWallPosition, isVertical } from "src/helpers/walls";
import { isFlatRoofType } from "src/helpers/roofType";
// @ts-ignore
import isEqual from "lodash/isEqual";

const EMPTY_SECTION_TYPE = "empty";
const KAPSCHUUR_EMPTY_SECTION_TYPE = "empty_no_corners";
const BEAM_SECTION_TYPE = "beam";
const CONCRETE_WOOD_COLUMN_TYPE = "concrete_wood";
const WOOD_COLUMN_TYPE = "wood";
const OPENING_SIDE_PADDING = 400; // 200mm on each side
const OPENING_BETWEEN_GAP = 400; // 400mm between door and window

export type ContainedImageRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function getContainedImageRect(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): ContainedImageRect {
  if (
    sourceWidth <= 0 ||
    sourceHeight <= 0 ||
    targetWidth <= 0 ||
    targetHeight <= 0
  ) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const scale = Math.min(
    targetWidth / sourceWidth,
    targetHeight / sourceHeight
  );
  const width = sourceWidth * scale;
  const height = sourceHeight * scale;

  return {
    x: (targetWidth - width) / 2,
    y: (targetHeight - height) / 2,
    width,
    height,
  };
}

class ConfiguratorService {
  private static instance: ConfiguratorService;
  private CONF: any;
  private APP: any;
  private MAP: any;
  private THREE: any;
  private state?: Configuration;

  constructor() {
    if (ConfiguratorService.instance) {
      return ConfiguratorService.instance;
    }

    ConfiguratorService.instance = this;

    if (!window.CONF || !window.CONF.house) {
      throw new Error("window.CONF is not available");
    }

    if (!window.APP) {
      throw new Error("window.APP is not available");
    }

    this.CONF = window.CONF;
    this.APP = window.APP;
    this.MAP = window.MAP;
    this.THREE = window.THREE;
  }

  /**
   * Retrieves the scene loader
   *
   * @return {Object}
   */
  getLoader() {
    return this.APP.loader;
  }

  /**
   * Checks if the scene has loaded the required (12) number of models.
   *
   * @return {boolean} Returns true if the number of loaded models is greater than or equal to 12, otherwise false.
   */
  isLoaded() {
    return this.APP.loader.models.size >= 10;
  }

  setState(state: Configuration) {
    this.state = state;
  }

  focusOnWall(wall: Wall): void {
    let configWall = this.findWallByPosition(wall.position);

    if (!configWall) {
      this.addWall(wall.position);
      this.CONF.onUpdate();
      configWall = this.findWallByPosition(wall.position);
    }

    if (configWall) this.APP.scene.focusOn(configWall.id, true);
  }

  zoomIn(): void {
    this.APP.scene.camera.translateZ(-1);
  }

  zoomOut(): void {
    this.APP.scene.camera.translateZ(1);
  }

  // zadeldak - 3 angle roof, platdak - flat roof
  changeRoofType(): void {
    if (!this.state) return;

    this.CONF.changeRoofType(this.state?.roofType);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  updateColumnTypes(): void {
    if (!this.state) return;

    const columns = [...this.CONF.house.columns];
    const columnType =
      this.state.roofType === "kapschuur" ? "empty_column" : "wood";

    columns.forEach((column) => {
      if (column.type === columnType) {
        return;
      }

      this.CONF.setColumnType(column.id, columnType);
    });

    this.CONF.onUpdate();
    this.fixVertex();
  }

  getColumnsBalks(): number {
    return this.CONF.house.columns.length;
  }

  /**
   * Calculates and returns the number of columns based on the configuration.
   *
   * @return {number}
   */
  getColumns(): number {
    return this.getHouse().frontLength.length;
  }

  getRows(): number {
    return 1;
  }

  changeSize(): void {
    if (!this.state) return;
    const offsetBack = 350;
    const offsetFront = 350;
    const offsetLeft = 350;
    const offsetRight = 350;

    const col_size = 150;

    // Depth <= 250cm → max width span 400cm, Depth 251-400cm → max width span 350cm
    const depth = this.state.dimensions.depth;
    const maxWidthSpan = depth <= 2500 + col_size * 2 ? 4000 : 3500;
    this.CONF.changeLimits({ width: maxWidthSpan, depth: 4000 });


    this.CONF.changeSize({
      width: this.state.dimensions.width,
      depth: this.state.dimensions.depth,
      height: this.state.dimensions.height,
      front: this.state.dimensions.overhangFront + offsetFront,
      back: this.state.dimensions.overhangBack + offsetBack,
      left: this.state.dimensions.overhangLeft + offsetLeft,
      right: this.state.dimensions.overhangRight + offsetRight,
    });
    this.CONF.onUpdate();
    this.fixVertex();
  }

  changeStorageWidth(): void {
    if (!this.state) return;
    const balkWidth = 150;

    let leftStorageWidth = this.state.storages.left
      ? this.state.dimensions.leftStorageWidth - balkWidth * 2
      : 0;
    let rightStorageWidth = this.state.storages.right
      ? this.state.dimensions.rightStorageWidth - balkWidth * 2
      : 0;

    this.CONF.setFixedLength({
      left: leftStorageWidth,
      right: rightStorageWidth,
      front: 0,
      back: 0,
    });

    this.CONF.onUpdate();
  }

  toggleGutter(): void {
    if (!this.state) return;
    this.CONF.enableGutter(0, this.state.gutter.enabled);
    this.CONF.enableGutter(1, this.state.gutter.enabled);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  updateGutterMaterial(): void {
    if (!this.state) return;
    this.CONF.updateGutterMaterial(0, this.state.gutter.type);
    this.CONF.updateGutterMaterial(1, this.state.gutter.type);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  updateGutterOversize(oversize: GutterOversize): void {
    this.CONF.updateGutterOversize(0, oversize);
    this.CONF.updateGutterOversize(1, oversize);
    this.fixVertex();
  }

  changeRoofMaterial(): void {
    if (!this.state) return;
    let roofMaterial = this.state.roof.type;
    const existingCustomizations = ["blackRoof", "terracottaRoof", "groenRoof"];
    const customizations = this.CONF.house.roof.customization.filter(
      (item: string) => !existingCustomizations.includes(item)
    );

    if (roofMaterial === "dakpanplaat") {
      customizations.push("blackRoof");
    } else if (roofMaterial === "dakpanplaat_rood") {
      roofMaterial = "dakpanplaat";
      customizations.push("terracottaRoof");
    } else if (roofMaterial === "leiendak_green") {
      roofMaterial = "leiendak";
      customizations.push("groenRoof");
    } else if (roofMaterial === "leiendak_red") {
      roofMaterial = "leiendak";
      customizations.push("terracottaRoof");
    }

    this.CONF.changeRoofMaterial(roofMaterial);
    this.CONF.changeRoofCustomization(customizations);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  changeRoofCustomization(customization: string[]): void {
    if (!this.CONF.house.roof.customization) {
      this.CONF.house.roof.customization = [];
    }

    const trimCustomizations = new Set(["metalic_ring", "daktrim_empty"]);
    const hasMetalicRing = customization.includes("metalic_ring");
    const hasEmptyDaktrim = customization.includes("daktrim_empty");
    const shouldClearTrim =
      customization.includes("") || customization.length === 0;

    if (shouldClearTrim || hasMetalicRing || hasEmptyDaktrim) {
      this.CONF.house.roof.customization =
        this.CONF.house.roof.customization.filter(
          (item: string) => !trimCustomizations.has(item)
        );
    }

    if (hasMetalicRing) {
      this.CONF.house.roof.customization.push("metalic_ring");
    } else if (hasEmptyDaktrim) {
      this.CONF.house.roof.customization.push("daktrim_empty");
    }

    this.CONF.onUpdate();
    this.fixVertex();
  }

  changeShorenType(type: "default" | "filled"): void {
    if (typeof this.CONF.changeShorenType !== "function") return;
    this.CONF.changeShorenType(type);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  setColumnType(columnId: string, type: string): void {
    this.CONF.setColumnType(columnId, type);
    this.CONF.onUpdate();
  }

  changePanel(
    index: number,
    name: string,
    rotate: number,
    customization: string[]
  ): void {
    this.CONF.changePanel(index, name, rotate, customization);
    this.CONF.onUpdate();
    this.fixVertex();
  }

  findWallByPosition(position: Position) {
    return this.CONF.house.walls.find(
      (section: any) =>
        this.isPositionMatch(section, position) ||
        this.isPositionMatch(section, {
          start: position.end,
          end: position.start,
        })
    );
  }

  addWall(position: Position) {
    this.CONF.addWall(position.start, position.end);

    return this.findWallByPosition(position);
  }

  removeWall(wallId: number): void {
    this.CONF.removeWall(wallId);
  }

  isPositionMatch = (section: any, position: Position) => {
    return (
      section.start.x === position.start.x &&
      section.start.y === position.start.y &&
      section.end.x === position.end.x &&
      section.end.y === position.end.y
    );
  };

  addElement(
    wallId: number,
    wallName: string,
    customizations: string[] = [],
    separator: string = BEAM_SECTION_TYPE
  ) {
    if (!isFlatRoofType(this.state?.roofType) && !wallName.includes("empty"))
      return;
    const inserts: WallInsertObject[] = [];
    this.CONF.addSectionToWall(
      wallId,
      wallName,
      customizations,
      inserts,
      false,
      separator
    );
  }

  addKapschuurElement(
    wallId: number,
    color: string,
    innerColor: string,
    door: string,
    window: string,
    side: string,
    doorSide: string,
    customizations: string[],
    separator: string = BEAM_SECTION_TYPE
  ) {
    // if (this.state?.roofType !== "kapschuur") return;

    const inserts: WallInsertObject[] = [];
    color = "kapschuur_wall_" + color;

    if (color === "kapschuur_potdeksel" && innerColor !== "empty") {
      customizations.push("innerWall");
    }

    if (window === "raam") {
      inserts.push({
        id: 1,
        type: "window",
        modelName: "raam",
        position: {
          x: 0,
          y: 1.01,
          z: 0,
        },
        size: {
          width: 800,
          height: 1000,
          depth: 100,
        },
      });
    }

    if (window === "vleugelraam") {
      inserts.push({
        id: 2,
        type: "window",
        modelName: "vleugelraam_4vaks",
        position: {
          x: 0,
          y: 1.235,
          z: 0,
        },
        size: {
          width: 1000,
          height: 775,
          depth: 115,
        },
      });
    }

    if (door === "dichte_73cm" || door === "dichte_100cm") {
      inserts.push({
        id: 3,
        type: "door",
        modelName: "dichte_deur",
        rotatedDoor: doorSide === "left",
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        size: {
          width: 1000,
          height: 2010,
          depth: 158,
        },
      });
    }

    if (door === "stapeldorpeldeur") {
      inserts.push({
        id: 6,
        type: "door",
        modelName: "stapeldorpeldeur",
        rotatedDoor: doorSide === "left",
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        size: {
          width: 1000,
          height: 2010,
          depth: 151,
        },
      });
    }

    if (door === "dubbele_dichte") {
      inserts.push({
        id: 4,
        type: "door",
        modelName: "dubbele_dichte_deur",
        rotatedDoor: doorSide === "left",
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        size: {
          width: 1900,
          height: 2010,
          depth: 158,
        },
      });
    }

    if (door === "dubble_stapeldorpeldeur") {
      inserts.push({
        id: 5,
        type: "door",
        modelName: "dubbele_stapeldorpeldeur",
        rotatedDoor: doorSide === "left",
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        size: {
          width: 2000,
          height: 2010,
          depth: 151,
        },
      });
    }

    this.CONF.addSectionToWall(
      wallId,
      color,
      customizations,
      inserts,
      side !== "left",
      separator
    );
  }

  getWallWidth(wall: Wall) {
    const configWall = this.findWallByPosition(wall.position);

    if (!configWall?.sections) return 0;

    return configWall.sections.reduce(
      (width: number, element: { type: string; length: number }) => {
        if (
          element.type.includes("potdeksel") ||
          element.type.includes("blockhut")
        ) {
          width += element.length;
        }

        return width;
      },
      0
    );
  }

  getHouse() {
    return this.CONF.house;
  }

  canAddSectionToWall(
    wallId: number,
    sectionType: string,
    useFullWall: boolean = false,
    quantityElements: number = 0
  ): boolean {
    const sectionData = this.CONF.getSectionData(sectionType);

    if (!sectionData) {
      console.error(
        `Section data with type name ${sectionType} not found 2222`
      );
      return false;
    }

    /** HARDCODE to remove */
    const separatorData = this.CONF.getSectionData(
      this.CONF.parameters.wallSeparatorType
    );

    if (!separatorData) {
      console.error(
        `Separator type '${this.CONF.parameters.wallSeparatorType}' section data not found. Check sections data`
      );

      return false;
    }

    const wall = this.CONF.getWallById(wallId);

    if (!wall) {
      console.error(`Wall with id '${wallId}' not found`);
      return false;
    }

    let usedSpace = 0;
    let index = 0;

    if (!useFullWall) {
      wall.sections.forEach((section: { type: any; length: number }) => {
        if (
          section.type !== "empty" &&
          section.type !== "empty_no_corners" &&
          index < quantityElements
        ) {
          usedSpace += section.length;
        }

        if (section.length > 200) {
          index++;
        }
      });
    }

    let freeSpace = this.getWallLength(wall) - usedSpace;

    let sectionMinSize = sectionData.size.min;

    if (sectionMinSize === 0) {
      if (
        freeSpace < 2000 &&
        (sectionType.includes("dubbele") || sectionType.includes("dubble"))
      ) {
        return false;
      }

      if (
        freeSpace < 2000 &&
        sectionType.includes("door") &&
        sectionType.includes("window")
      ) {
        return false;
      }

      if (
        freeSpace < 1000 &&
        (sectionType.includes("dichte_73cm") ||
          sectionType.includes("dichte_100cm") ||
          sectionType.includes("stapeldorpeldeur"))
      ) {
        return false;
      }

      if (
        freeSpace < 1000 &&
        (sectionType.includes("raam") || sectionType.includes("vleugelraam"))
      ) {
        return false;
      }
    }

    return freeSpace >= sectionMinSize;
  }

  configuratorWallExist(wallId: number) {
    return (
      window.CONF.house.walls.find(
        (wall: { id: number }) => wall.id === wallId
      ) !== undefined
    );
  }

  getWallLength(wall: { start: Coordinate; end: Coordinate }): number {
    const house = this.CONF.house;

    if (isVertical(wall.start, wall.end)) {
      const i = Math.min(wall.start.y, wall.end.y);
      return house.sideLength[i];
    } else {
      const i = Math.min(wall.start.x, wall.end.x);
      return house.frontLength[i];
    }
  }

  enableParapet() {
    if (!this.state) return;
    for (let column of this.CONF.house.columns) {
      if (this.state.parapet.enabled && column.type === WOOD_COLUMN_TYPE) {
        this.CONF.setColumnType(column.id, "concrete_wood");
        continue;
      }

      if (
        !this.state.parapet.enabled &&
        column.type === CONCRETE_WOOD_COLUMN_TYPE
      ) {
        this.CONF.setColumnType(column.id, WOOD_COLUMN_TYPE);
      }
    }

    this.CONF?.onUpdate();
  }

  setParapet() {
    if (!this.state) return;

    for (let wall of this.CONF.house.walls) {
      const elements = wall.sections;
      window.CONF.removeSectionsFromWall(wall.id);

      for (let element of elements) {
        const customization = element.customization;
        let type = element.type;

        if (type.startsWith("brick")) {
          type = type.replace("wall", "brick");
        }

        if (
          this.state.parapet.type === "vezelplaat" &&
          !customization.includes("vezelplaat")
        ) {
          customization.push("vezelplaat");
        }

        if (
          this.state.parapet.type === "metselwerk" &&
          customization.includes("vezelplaat")
        ) {
          customization.splice(customization.indexOf("vezelplaat"), 1);
        }

        this.addElement(
          wall.id,
          type,
          customization,
          CONCRETE_WOOD_COLUMN_TYPE
        );
      }
    }
  }

  setRoofing(roofing: string) {
    this.CONF.changeRoofMaterial(roofing);
    this.CONF.onUpdate();
  }

  setRoofWall(side: string) {
    if (!this.state) return;
    const sideId = side === "left" ? 0 : 1;
    const roofWall =
      side === "left"
        ? this.state.roof.walls.left
        : this.state.roof.walls.right;
    const customization = [];
    let name = roofWall.name;

    if (roofWall.name === "wall" || roofWall.name === "1window") {
      if (roofWall.innerWallEnabled) name = "wall_2";
      if (roofWall.outerWallType === "zwart") customization.push("dark");
      if (roofWall.innerWallEnabled && roofWall.innerWallType === "zwart")
        customization.push("dark_inner");
    }

    if (roofWall.name === "1window") {
      if (roofWall.innerWallEnabled) name = "1window_2";
      if (roofWall.window === "wit") customization.push("white_window");
      if (roofWall.window === "zwart") customization.push("dark_window");
      if (roofWall.window === "houtkleur") customization.push("window_wood");
    }

    this.CONF.changePanel(sideId, name, 0, customization);
    this.CONF.onUpdate();
  }

  async getScreenShots() {
    this.deselectWalls();
    this.deselectBerging("left");
    this.deselectBerging("right");

    const currentRuler = this.CONF.house.ruler.type ?? "none";

    this.setRuler("none");

    const originalTarget = this.APP.scene.controls.target.clone();
    const originalPosition = this.APP.scene.camera.position.clone();
    const originalRotation = this.APP.scene.camera.rotation.clone();
    const originalFov = this.APP.scene.camera.fov;

    const boundingBox = new this.THREE.Box3().setFromObject(this.APP.scene);
    const size = new this.THREE.Vector3();
    boundingBox.getSize(size);

    const center = new this.THREE.Vector3();
    boundingBox.getCenter(center);

    // Screenshots size
    const screenshotWidth = 483 * 2;
    const screenshotHeight = 255 * 2;

    // Save current render settings
    const renderer = this.APP.renderer;
    const originalSize = renderer.getSize(new this.THREE.Vector2());
    const originalPixelRatio = renderer.getPixelRatio();

    // Set screenshot size to render
    renderer.setSize(screenshotWidth, screenshotHeight);
    renderer.setPixelRatio(1);

    // set camera
    const aspect = screenshotWidth / screenshotHeight;

    this.APP.scene.camera.aspect = aspect;
    this.APP.scene.controls.target.set(0, 1.5, 0);
    this.APP.scene.camera.updateProjectionMatrix();

    const screenshots = [];

    const targetPositions = [
      { x: 0, y: 1.5, z: 11 }, // front
      { x: 11, y: 1.7, z: 0 }, // left
      { x: -11, y: 1.7, z: 0 }, // right
      { x: 6, y: 1.5, z: 0 }, // inner
      { x: 0, y: 1.5, z: -11 }, // back
    ];

    const columns = this.getColumns();
    const wall = this.state?.walls.find((wall) => wall.id === `v-0-${columns}`);

    for (let i = 0; i < targetPositions.length; i++) {
      const pos = targetPositions[i];

      try {
        if (i === 3) {
          if (wall) {
            const configWall = this.findWallByPosition(wall.position);

            if (configWall) {
              this.CONF.removeSectionsFromWall(configWall.id);
              this.addElement(configWall.id, "empty_no_corners");
              this.CONF.onUpdate();
            }
          }
        }

        this.APP.scene.camera.position.set(pos.x, pos.y, pos.z);
        this.APP.scene.camera.lookAt(center.x, center.y - 100, center.z);
        this.APP.scene.camera.updateMatrixWorld(true);

        await new Promise((resolve) => setTimeout(resolve, 300));
        renderer.render(this.APP.scene, this.APP.scene.camera);

        const canvas = renderer.domElement;
        const screenshot = canvas.toDataURL("image/png");
        screenshots.push(screenshot);

        if (i === 3) {
          if (wall) {
            this.updateWall(wall);
            this.CONF.onUpdate();
          }
        }
      } catch (err) {
        console.error(`Error capturing screenshot for position #${i}:`, err);
        screenshots.push(null);
      }
    }

    // restore render settings
    renderer.setSize(originalSize.x, originalSize.y);
    renderer.setPixelRatio(originalPixelRatio);
    this.APP.scene.camera.fov = originalFov;
    this.APP.scene.camera.aspect = originalSize.x / originalSize.y;
    this.APP.scene.controls.target.copy(originalTarget);
    this.APP.scene.camera.position.copy(originalPosition);
    this.APP.scene.camera.rotation.copy(originalRotation);
    this.APP.scene.camera.updateProjectionMatrix();

    const map = await this.convertSvgToPng(
      this.MAP.container,
      screenshotWidth,
      screenshotHeight
    ); // svg
    screenshots.push(map);

    this.setRuler(currentRuler);

    // const link = document.createElement("a");
    //
    // screenshots.forEach((screenshot, i) => {
    //     link.href = screenshot!;
    //     link.download = `screenshot-${i + 1}.png`;
    //     link.click();
    //     console.log(`Screenshot ${i + 1} captured.`);
    // });

    return screenshots;
  }

  async makeScreenShot() {
    this.deselectWalls();
    const originalTarget = this.APP.scene.controls.target.clone();
    const originalPosition = this.APP.scene.camera.position.clone();
    const originalRotation = this.APP.scene.camera.rotation.clone();
    const originalFov = this.APP.scene.camera.fov;

    const boundingBox = new this.THREE.Box3().setFromObject(this.APP.scene);
    const size = new this.THREE.Vector3();
    boundingBox.getSize(size);

    const center = new this.THREE.Vector3();
    boundingBox.getCenter(center);

    // Screenshots size
    const screenshotWidth = 483 * 3;
    const screenshotHeight = 255 * 3;

    // Save current render settings
    const renderer = this.APP.renderer;
    const originalSize = renderer.getSize(new this.THREE.Vector2());
    const originalPixelRatio = renderer.getPixelRatio();

    // Set screenshot size to render
    renderer.setSize(screenshotWidth, screenshotHeight);
    renderer.setPixelRatio(1);

    // set camera
    const aspect = screenshotWidth / screenshotHeight;
    this.APP.scene.camera.aspect = aspect;
    this.APP.scene.controls.target.set(0, 1.5, 0);
    this.APP.scene.camera.updateProjectionMatrix();

    const screenshots = [];

    const targetPositions = [
      { x: 4, y: 1.5, z: 11 }, // front
    ];

    for (let i = 0; i < targetPositions.length; i++) {
      const pos = targetPositions[i];

      try {
        this.APP.scene.camera.position.set(pos.x, pos.y, pos.z);
        this.APP.scene.camera.lookAt(center.x, center.y - 100, center.z);
        this.APP.scene.camera.updateMatrixWorld(true);

        await new Promise((resolve) => setTimeout(resolve, 300));
        renderer.render(this.APP.scene, this.APP.scene.camera);

        const canvas = renderer.domElement;
        const screenshot = canvas.toDataURL("image/png");
        screenshots.push(screenshot);
      } catch (err) {
        console.error(`Error capturing screenshot for position #${i}:`, err);
        screenshots.push(null);
      }
    }

    // restore render settings
    renderer.setSize(originalSize.x, originalSize.y);
    renderer.setPixelRatio(originalPixelRatio);
    this.APP.scene.camera.fov = originalFov;
    this.APP.scene.camera.aspect = originalSize.x / originalSize.y;
    this.APP.scene.controls.target.copy(originalTarget);
    this.APP.scene.camera.position.copy(originalPosition);
    this.APP.scene.camera.rotation.copy(originalRotation);
    this.APP.scene.camera.updateProjectionMatrix();

    const link = document.createElement("a");

    screenshots.forEach((screenshot, i) => {
      link.href = screenshot!;
      link.download = `screenshot-${i + 1}.png`;
      link.click();
      // console.log(`Screenshot ${i + 1} captured.`);
    });

    return screenshots;
  }

  updateSlidingGlassWalls(walls: Wall[]) {
    if (!this.state) return walls;

    return walls.map((wall) => {
      if (wall.slidingGlassWall === "empty") return wall;
      const glassDoor = this.getSlidingGlassDoor(wall);
      return { ...wall, slidingGlassWall: glassDoor };
    });
  }

  getSlidingGlassDoor(wall: Wall) {
    if (!this.state || !isFlatRoofType(this.state.roofType)) return "empty";

    let configWall = this.findWallByPosition(wall.position);

    if (!configWall) {
      if (!wall.inner) return "empty";

      this.addWall(wall.position);
      configWall = this.findWallByPosition(wall.position);
    }

    if (
      configWall &&
      this.canAddSectionToWall(configWall.id, "glass_door_4m", true)
    ) {
      return "glass_door_4m";
    } else if (
      configWall &&
      this.canAddSectionToWall(configWall.id, "glass_door_3m", true)
    ) {
      return "glass_door_3m";
    } else if (
      configWall &&
      this.canAddSectionToWall(configWall.id, "glass_door_2m", true)
    ) {
      return "glass_door_2m";
    }

    return "empty";
  }

  updateDoorWindowWalls(
    walls: Wall[],
    shouldValidate?: (wall: Wall) => boolean
  ) {
    if (!walls) return walls;

    const shouldValidateWall = shouldValidate ?? (() => true);

    return walls.map((wall: Wall) => {
      if (!shouldValidateWall(wall)) {
        return wall;
      }

      const woodWall = { ...wall.woodWall };
      const newWall = { ...wall, woodWall };

      if (this.state?.roofType === "kapschuur" && wall?.id.startsWith("h-1")) {
        woodWall.door = "empty";
        woodWall.window = "empty";
        newWall.woodWall = woodWall;

        return newWall;
      }

      if (woodWall.door !== "empty" && woodWall.window !== "empty") {
        if (!this.canAddDoorWindow(newWall)) {
          woodWall.window = "empty";
        }

        if (!this.canAddDoorWindow(newWall)) {
          woodWall.door = "empty";
        }

        newWall.woodWall = woodWall;

        return newWall;
      }

      if (woodWall.door !== "empty") {
        if (!this.canAddDoorWindow(newWall)) {
          woodWall.door = "empty";
        }

        newWall.woodWall = woodWall;

        return newWall;
      }

      if (woodWall.window !== "empty") {
        if (!this.canAddDoorWindow(newWall)) {
          woodWall.window = "empty";
        }

        newWall.woodWall = woodWall;

        return newWall;
      }

      return wall;
    });
  }

  updateCommonStoragesWalls(walls: Wall[], prevWalls: Wall[]) {
    if (!this.state || !(this.state.storages.left && this.state.storages.right))
      return walls;

    const leftStorageWall = walls.find(
      (wall: Wall) => wall.storage === "left_right"
    );
    const prevLeftStorageWall = prevWalls.find(
      (wall: Wall) => wall.storage === "left_right"
    );
    const rightStorageWall = walls.find(
      (wall: Wall) => wall.storage === "right_left"
    );
    const prevRightStorageWall = prevWalls.find(
      (wall: Wall) => wall.storage === "right_left"
    );
    const isCommonWall = leftStorageWall?.id === rightStorageWall?.id;
    const changedLeft = isEqual(rightStorageWall, prevRightStorageWall);
    const changedRight = isEqual(leftStorageWall, prevLeftStorageWall);

    if (!isCommonWall || !leftStorageWall || !rightStorageWall) return walls;

    if (changedLeft) {
      rightStorageWall.woodWall = { ...leftStorageWall.woodWall };
    } else if (changedRight) {
      leftStorageWall.woodWall = { ...rightStorageWall.woodWall };
    } else {
      rightStorageWall.woodWall = { ...leftStorageWall.woodWall };
    }

    return walls;
  }

  updateWalls() {
    if (!this.state) return;
    const columns = this.getColumns();
    this.removeReversedLeftStorageWall();
    this.removeReversedRightStorageWall(columns);

    for (let i = 2; i <= columns; i++) {
      window.CONF.changePanel(i, "empty", 0, []);
    }

    this.state.walls.forEach((wall) => {
      if (!this.state) return;

      if (
        this.state.storages.left &&
        this.state.storages.right &&
        "v-0-1" === `v-0-${columns - 1}` &&
        wall.id === "v-0-1" &&
        wall.storage === "right_left"
      ) {
        return;
      }

      if (
        this.state.storages.right &&
        wall.storage === "empty" &&
        columns === 2 &&
        (wall.id === "h-0-1" || wall.id === "h-1-1" || wall.id === "v-0-1")
      ) {
        return;
      }

      if (
        wall.inner &&
        (wall.id === "v-0-1" || wall.id === "v-0-2" || wall.id === "v-0-3")
      ) {
        if (this.state.storages.left || this.state.storages.right) {
          this.changePanel(
            columns + 1 - wall.position.start.x,
            "bottom_bulk",
            0,
            []
          );
        } else {
          this.changePanel(wall.position.start.x + 1, "bottom_bulk", 0, []);
        }
      }

      this.updateWall(wall);
    });

    const tempConfWalls = [...this.CONF.house.walls];

    // Remove deleted walls
    tempConfWalls.forEach(
      (configWall: {
        id: number;
        start: { x: number; y: number };
        end: { x: number; y: number };
      }) => {
        if (!this.state) return;

        const w = this.state.walls.find((wall) => {
          const pos = wall.position;
          const start = configWall.start;
          const end = configWall.end;
          return (
            (start.x === pos.start.x &&
              start.y === pos.start.y &&
              end.x === pos.end.x &&
              end.y === pos.end.y) ||
            (end.x === pos.start.x &&
              end.y === pos.start.y &&
              start.x === pos.end.x &&
              start.y === pos.end.y)
          );
        });

        if (w) return;

        window.CONF.removeSectionsFromWall(configWall.id);
        this.addElement(
          configWall.id,
          isFlatRoofType(this.state.roofType)
            ? EMPTY_SECTION_TYPE
            : KAPSCHUUR_EMPTY_SECTION_TYPE
        );

        // delete empty inner walls
        if (
          configWall.start.x === configWall.end.x &&
          configWall.start.x !== 0 &&
          configWall.start.x !== columns
        ) {
          this.removeWall(configWall.id);
        }
      }
    );

    window.CONF.onUpdate();
    this.fixVertex();
  }

  updateRightStoragePositions(walls: Wall[]) {
    if (!this.state) return walls;
    const columns = this.getColumns();

    return walls.map((wall: Wall) => {
      if (wall.storage === "right_left") {
        const position = {
          start: {
            x: columns - 1,
            y: 1,
          },
          end: { x: columns - 1, y: 0 },
        };
        return { ...wall, position, id: `v-0-${columns - 1}` };
      } else if (wall.storage === "right_right") {
        let position = getWallPosition(`v-0-${columns}`);
        return { ...wall, position, id: `v-0-${columns}` };
      } else if (wall.storage === "right_front") {
        const position = getWallPosition(`h-0-${columns - 1}`);
        return { ...wall, position, id: `h-0-${columns - 1}` };
      } else if (wall.storage === "right_back") {
        const position = getWallPosition(`h-1-${columns - 1}`);
        return { ...wall, position, id: `h-1-${columns - 1}` };
      } else {
        return wall;
      }
    });
  }

  updateRightSideWallPositions(walls: Wall[]) {
    if (!this.state || this.state.storages.right) return walls;
    const columns = this.getColumns();

    return walls
      .map((wall: Wall) => {
        if (!wall.isRightSide) {
          return wall;
        }

        const position = getWallPosition(`v-0-${columns}`);

        return { ...wall, position, id: `v-0-${columns}` };
      })
      .filter(
        (wall: Wall) =>
          !(wall.id === `v-0-${columns}` && !wall.isRightSide) ||
          wall.storage === "left_right"
      );
  }

  private removeReversedRightStorageWall(columns: number) {
    if (!this.state?.storages.right) return;
    const storageColumn = Math.max(columns - 1, 0);

    const searchPosition: Position = {
      start: { x: storageColumn, y: 0 },
      end: { x: storageColumn, y: 1 },
    };
    const flippedPosition: Position = {
      start: { x: storageColumn, y: 1 },
      end: { x: storageColumn, y: 0 },
    };

    const matchedWalls = this.CONF.house.walls.filter(
      (configWall: ConfigWall) =>
        this.isPositionMatch(configWall, searchPosition) ||
        this.isPositionMatch(configWall, flippedPosition)
    );

    if (!matchedWalls.length) return;

    const templateWall =
      matchedWalls.find((configWall: ConfigWall) =>
        this.isPositionMatch(configWall, flippedPosition)
      ) || matchedWalls[0];

    matchedWalls.forEach((matchedWall: ConfigWall) => {
      this.removeWall(matchedWall.id);
    });

    const newWall = this.addWall(flippedPosition);
    if (!newWall) return;

    const clonedTemplate = JSON.parse(
      JSON.stringify(templateWall)
    ) as ConfigWall;
    const newWallId = newWall.id;

    Object.assign(newWall, clonedTemplate, {
      id: newWallId,
      start: { ...flippedPosition.start },
      end: { ...flippedPosition.end },
    });
  }

  private removeReversedLeftStorageWall() {
    if (!this.state?.storages.left) return;
    const position = getWallPosition("v-0-1");
    const storageColumn = position.start.x;

    const reversedWall = this.CONF.house.walls.find(
      (configWall: ConfigWall) =>
        configWall.start.x === storageColumn &&
        configWall.end.x === storageColumn &&
        configWall.start.y === position.end.y &&
        configWall.end.y === position.start.y
    );

    if (reversedWall) {
      this.removeWall(reversedWall.id);
    }
  }

  private updateWall(newwall: Wall) {
    const wall =
      newwall.woodWall.outerColor === "wall_vuren_black"
        ? {
          ...newwall,
          woodWall: {
            ...newwall.woodWall,
            outerColor: "wall_potdeksel_black",
          },
        }
        : { ...newwall };
    let configWall = this.findWallByPosition(wall.position);

    // add inner wall
    if (!configWall && wall.inner && !wall.woodWall.rotate) {
      configWall = this.addWall(wall.position);
    } else if (!configWall && wall.inner && wall.woodWall.rotate) {
      configWall = this.addWall({
        start: wall.position.end,
        end: wall.position.start,
      });
    }

    // fix vertical walls
    if (wall.id === "v-0-1") {
      if (
        configWall &&
        configWall.start.y === wall.position.start.y &&
        wall.storage !== "right_left"
      ) {
        this.removeWall(configWall.id);
        this.addWall({
          start: wall.position.end,
          end: wall.position.start,
        });

        configWall = this.findWallByPosition(wall.position);
      }
    }

    if (wall.id === "v-0-2" && wall.storage === "right_left") {
      if (configWall && configWall.start.y === wall.position.end.y) {
        this.removeWall(configWall.id);
        this.addWall(wall.position);
        configWall = this.findWallByPosition(wall.position);
      }
    }

    if (wall.id === "v-0-3" && wall.storage === "right_left") {
      if (configWall && configWall.start.y === wall.position.start.y) {
        this.removeWall(configWall.id);
        this.addWall({
          start: wall.position.end,
          end: wall.position.start,
        });
        configWall = this.findWallByPosition(wall.position);
      }
    }

    if (
      wall.id === "v-0-2" &&
      (wall.storage === "right_right" || wall.isRightSide)
    ) {
      if (configWall && configWall.start.y === wall.position.start.y) {
        this.removeWall(configWall.id);
        this.addWall({
          start: wall.position.end,
          end: wall.position.start,
        });
        configWall = this.findWallByPosition(wall.position);
      }
    }

    if (
      wall.id === "v-0-3" &&
      (wall.storage === "right_right" || wall.isRightSide)
    ) {
      if (configWall && configWall.start.y === wall.position.end.y) {
        this.removeWall(configWall.id);
        this.addWall(wall.position);
        configWall = this.findWallByPosition(wall.position);
      }
    }

    if (!configWall) {
      return;
    }

    const useBlackOuter = wall.woodWall.outerColor.endsWith("_black");
    const useBlackInner = wall.woodWall.innerColor.endsWith("_black");

    window.CONF.removeSectionsFromWall(configWall.id);
    window.CONF.setWallAlign(
      configWall.id,
      wall.woodWall.side === "right" ? 1 : 0
    );

    // set elements
    this.setElements(wall, configWall);

    const hasNonEmptyElement =
      wall.elementWall.elements.length > 0 &&
      wall.elementWall.elements.some((element) => element !== "empty");

    if (hasNonEmptyElement) {
      return;
    }

    // set sliding glass wall
    this.setSlidingGlassWall(wall, configWall);

    if (wall.slidingGlassWall !== "empty") return;

    if (wall.woodWall.outerColor === "empty") {
      // @ts-ignore
      this.addElement(
        configWall.id,
        // @ts-ignore
        isFlatRoofType(this.state?.roofType)
          ? EMPTY_SECTION_TYPE
          : KAPSCHUUR_EMPTY_SECTION_TYPE
      );
      return;
    }

    // set walls
    const customizations: string[] = [];

    if (useBlackOuter) {
      customizations.push("dark");
    }

    if (useBlackInner) {
      customizations.push("dark_inner");
    }

    if (wall.woodWall.innerColor !== "empty") customizations.push("innerWall");

    const color = (
      wall.woodWall.outerColor === "wall_vuren_black"
        ? "wall_potdeksel_black"
        : wall.woodWall.outerColor
    )
      .replace("_black", "")
      .replace("wall_", "");

    // FIX: Removed check for the possibility of inserting a wall
    this.addKapschuurElement(
      configWall.id,
      color,
      wall.woodWall.innerColor,
      wall.woodWall.door,
      wall.woodWall.window,
      wall.woodWall.side,
      wall.woodWall.doorSide,
      customizations
    );

    return;
  }

  private setElements(wall: Wall, configWall: { id: number }) {
    // set element
    wall.elementWall.elements.forEach((element: string) => {
      this.addElement(configWall.id, element);
    });

    // set wood
    const useBlackOuter = wall.elementWall.outerColor.includes("black");
    let outerColor = wall.elementWall.outerColor.replace("_black", "");

    if (outerColor !== "empty") {
      this.addElement(configWall.id, outerColor, useBlackOuter ? ["dark"] : []);
    }
  }

  private setSlidingGlassWall(wall: Wall, configWall: { id: number }) {
    if (wall.slidingGlassWall === "empty") {
      return;
    }

    // Add the actual glass door section (glass_door_2m, glass_door_3m, or glass_door_4m)
    // this.addElement(configWall.id, wall.slidingGlassWall, []);

    this.addElement(configWall.id, "glass_panel", [
      wall.slidingGlassWallOptions.glassColor,
    ]);
  }

  public getWallName(wall: Wall, removeWindow: boolean = true) {
    if (!this.state) return EMPTY_SECTION_TYPE;

    if (
      wall.woodWall.door === "empty" &&
      wall.woodWall.outerColor === "empty" &&
      wall.woodWall.window === "empty"
    ) {
      return isFlatRoofType(this.state.roofType)
        ? EMPTY_SECTION_TYPE
        : KAPSCHUUR_EMPTY_SECTION_TYPE;
    }

    let outerColor = wall.woodWall.outerColor.replace("_black", "");
    let innerColor = wall.woodWall.innerColor.replace("_black", "");

    if (outerColor === "wall_potdeksel" && innerColor !== "empty") {
      outerColor = "wall_potdeksel2";
    }

    // Empty Wall
    // if (wall.woodWall.door === "empty" && wall.woodWall.window === "empty") {
    //   if (outerColor === "wall_potdeksel") {
    //     return "wall_potdeksel_empty";
    //   }
    //
    //   return outerColor;
    // }

    // Change door dichte 73 cm on dichte 100 cm
    let door =
      wall.woodWall.door === "dichte_73cm"
        ? "dichte_100cm"
        : wall.woodWall.door;

    // Only Door
    if (door !== "empty" && wall.woodWall.window === "empty") {
      if (outerColor.startsWith("wall_blockhut") && door === "dichte_100cm") {
        return `${outerColor}_door_${door}_${wall.woodWall.doorSide}`;
      }

      if (
        door === "stapeldorpeldeur" &&
        !outerColor.startsWith("wall_potdeksel2")
      ) {
        return `${outerColor}_door_${door}_100cm_${wall.woodWall.doorSide}`;
      }

      if (door === "dubbele_dichte") {
        if (outerColor === "wall_potdeksel") {
          return `${outerColor}_door_${door}_${wall.woodWall.doorSide === "left" ? "right" : "left"
            }`;
        }

        if (outerColor === "wall_potdeksel2") {
          return `${outerColor}_dubble_dichte_${wall.woodWall.doorSide === "left" ? "right" : "left"
            }`;
        }
      }

      if (door === "dubble_stapeldorpeldeur") {
        return `${outerColor}_${door}_${wall.woodWall.doorSide}`;
      }

      return `${outerColor}_door_${door}_${wall.woodWall.doorSide}`;
    }

    // Only window + inner color
    if (
      outerColor === "wall_potdeksel2" &&
      door === "empty" &&
      wall.woodWall.window !== "empty"
    ) {
      return `${outerColor}_${wall.woodWall.window}`;
    }

    // Only window
    if (door === "empty" && wall.woodWall.window !== "empty") {
      return `${outerColor}_window_${wall.woodWall.window}`;
    }

    // Door + Window
    if (door !== "empty" && wall.woodWall.window !== "empty") {
      if (door === "dichte_100cm" && wall.woodWall.window === "vleugelraam") {
        let wallName = `${outerColor}_dichte_deur_100cm_${wall.woodWall.doorSide}_window_${wall.woodWall.window}_${wall.woodWall.side}`;

        if (outerColor === "wall_potdeksel2") {
          wallName = `${outerColor}_door_dichte_100cm_${wall.woodWall.doorSide}_window_vleugelraam_${wall.woodWall.side}`;
        }

        if (removeWindow && !this.canAddDoorWindow(wall)) {
          wallName = `${outerColor}_dichte_deur_100cm_${wall.woodWall.doorSide}`;
        }

        return wallName;
      }

      if (
        door === "stapeldorpeldeur" &&
        wall.woodWall.window === "vleugelraam" &&
        outerColor === "wall_potdeksel"
      ) {
        let wallName = `${outerColor}_door_stapeldorpeldeur_${wall.woodWall.doorSide}_window_vleugelraam_${wall.woodWall.side}`;

        if (removeWindow && !this.canAddDoorWindow(wall)) {
          wallName = `${outerColor}_stapeldorpeldeur_${wall.woodWall.doorSide}`;
        }

        return wallName;
      }

      if (
        door === "stapeldorpeldeur" &&
        wall.woodWall.window === "vleugelraam" &&
        outerColor === "wall_potdeksel2"
      ) {
        let wallName = `${outerColor}_door_stapeldorpeldeur_${wall.woodWall.doorSide}_window_vleugeraam_${wall.woodWall.side}`;

        if (removeWindow && !this.canAddDoorWindow(wall)) {
          wallName = `${outerColor}_stapeldorpeldeur_${wall.woodWall.doorSide}`;
        }

        return wallName;
      }

      if (door === "dubbele_dichte") {
        let wallName = `${outerColor}_door_${door}_${wall.woodWall.doorSide === "left" ? "right" : "left"
          }`;

        if (removeWindow && !this.canAddDoorWindow(wall)) {
          wallName = outerColor;
        }

        return wallName;
      }

      if (door === "dubble_stapeldorpeldeur") {
        let wallName = `${outerColor}_${door}_${wall.woodWall.doorSide}`;

        if (removeWindow && !this.canAddDoorWindow(wall)) {
          wallName = outerColor;
        }

        return wallName;
      }

      let wallName = `${outerColor}_door_${door}_${wall.woodWall.doorSide}_window_${wall.woodWall.window}_${wall.woodWall.side}`;

      if (removeWindow && !this.canAddDoorWindow(wall)) {
        wallName = `${outerColor}_door_${door}_${wall.woodWall.doorSide}`;

        if (door === "stapeldorpeldeur") {
          wallName = `${outerColor}_door_${door}_100cm_${wall.woodWall.doorSide}`;
        }
      }

      return wallName;
    }

    return isFlatRoofType(this.state.roofType)
      ? EMPTY_SECTION_TYPE
      : KAPSCHUUR_EMPTY_SECTION_TYPE;
  }

  public getEmptyWallsCount(): number {
    if (!this.state) return 0;

    let count = 0;

    this.CONF.house.walls.forEach((wall: ConfigWall) => {
      if (
        Array.isArray(wall.sections) &&
        wall.sections.length === 1 &&
        wall.sections[0].type.startsWith("empty")
      ) {
        count++;
      }
    });

    return count;
  }

  /**
   * Get the width of a door in millimeters
   */
  public getDoorWidth(doorValue: string): number {
    switch (doorValue) {
      case "dichte_73cm":
      case "dichte_100cm":
      case "stapeldorpeldeur":
        return 1000;
      case "dubbele_dichte":
        return 2300;
      case "dubble_stapeldorpeldeur":
        return 2400;
      default:
        return 0;
    }
  }

  /**
   * Get the width of a window in millimeters
   */
  public getWindowWidth(windowValue: string): number {
    switch (windowValue) {
      case "raam":
        return 830;
      case "vleugelraam":
        return 1000;
      default:
        return 0;
    }
  }

  /**
   * Get the section width for a wall in millimeters
   */
  public getSectionWidth(wall: Wall): number {
    if (wall.id.startsWith("h-")) {
      const frontLength = this.CONF.house.frontLength;

      if (wall.id === "h-0-0" || wall.id === "h-1-0") {
        return frontLength[0];
      } else if (wall.id === "h-0-1" || wall.id === "h-1-1") {
        return frontLength[1];
      } else if (wall.id === "h-0-2" || wall.id === "h-1-2") {
        return frontLength[2];
      } else if (wall.id === "h-0-3" || wall.id === "h-1-3") {
        return frontLength[3];
      }
    } else if (wall.id.startsWith("v-")) {
      return this.CONF.house.depth;
    }

    return 0;
  }

  /**
   * Check if a door can fit in the section considering 200mm padding on each side
   */
  public canAddDoor(doorValue: string, sectionWidth: number): boolean {
    if (doorValue === "empty") return true;
    const doorWidth = this.getDoorWidth(doorValue);
    return sectionWidth >= doorWidth + OPENING_SIDE_PADDING;
  }

  /**
   * Check if a window can fit in the section considering 200mm padding on each side
   */
  public canAddWindow(windowValue: string, sectionWidth: number): boolean {
    if (windowValue === "empty") return true;
    const windowWidth = this.getWindowWidth(windowValue);
    return sectionWidth >= windowWidth + OPENING_SIDE_PADDING;
  }

  /**
   * Check if both door and window can fit together in the section
   * with 200mm padding on each side and 400mm between openings
   */
  public canAddDoorWindow(wall: Wall) {
    if (!this.state) return false;

    const sectionWidth = this.getSectionWidth(wall);
    if (sectionWidth === 0) return false;

    const door = wall.woodWall.door;
    const window = wall.woodWall.window;
    // Special case: kapschuur roof type on h-1 walls - no doors allowed
    if (this.state?.roofType === "kapschuur" && wall.id.startsWith("h-1")) {
      return door === "empty" && window === "empty";
    }

    // If no door and no window, it's always valid
    if (door === "empty" && window === "empty") {
      return true;
    }

    // Calculate total width needed
    const doorWidth = this.getDoorWidth(door);
    const windowWidth = this.getWindowWidth(window);
    const totalWidth = doorWidth + windowWidth;

    // Check if both can fit together with padding
    if (door !== "empty" && window !== "empty") {
      return (
        sectionWidth >=
        totalWidth + OPENING_SIDE_PADDING + OPENING_BETWEEN_GAP
      );
    }

    // Check if door alone can fit
    if (door !== "empty" && window === "empty") {
      return this.canAddWindow(
        window,
        sectionWidth - doorWidth - OPENING_SIDE_PADDING
      );
    }

    // Check if window alone can fit
    if (door === "empty" && window !== "empty") {
      return this.canAddDoor(
        door,
        sectionWidth - windowWidth - OPENING_SIDE_PADDING
      );
    }

    return false;
  }

  async convertSvgToPng(
    svgElement: SVGElement,
    outputWidth: number,
    outputHeight: number
  ): Promise<string> {
    async function inlineExternalImages(svgElement: SVGElement) {
      const images = svgElement.querySelectorAll("image");

      const promises = Array.from(images).map(async (image) => {
        const href = image.getAttribute("href");

        try {
          // @ts-ignore
          const response = await fetch(href);
          const blob = await response.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });

          // @ts-ignore
          image.setAttribute("href", dataUrl);
        } catch (error) {
          console.error(`Failed to inline image: ${href}`, error);
        }
      });

      await Promise.all(promises);
    }

    await inlineExternalImages(svgElement);

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: "image/svg+xml;charset=utf-8",
    });
    const svgUrl = URL.createObjectURL(svgBlob);

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const context = canvas.getContext("2d");

        if (!context) {
          URL.revokeObjectURL(svgUrl);
          reject(new Error("Unable to create a 2D canvas context"));
          return;
        }

        const rect = getContainedImageRect(
          img.naturalWidth || img.width,
          img.naturalHeight || img.height,
          canvas.width,
          canvas.height
        );

        context.drawImage(img, rect.x, rect.y, rect.width, rect.height);
        const pngDataUrl = canvas.toDataURL("image/png");
        URL.revokeObjectURL(svgUrl);
        resolve(pngDataUrl);
      };

      img.onerror = (error) => {
        URL.revokeObjectURL(svgUrl);
        reject(error);
      };

      img.src = svgUrl;
    });
  }

  fixVertex() {
    if (!this.state) return;
    const prevHeight = this.CONF.house.previousHeight / 1000;
    const depth = this.CONF.house.depth / 1000;
    const prevDepth = this.CONF.house.previousDepth / 1000;
    const angle20 = (20 * Math.PI) / 180;

    this.CONF.house.previousDepth = this.CONF.house.depth; // fix bug for kapschuur :(
    this.CONF.house.previousWidth = this.CONF.house.width;
    const heightThoughDepth = Math.tan(angle20) * (depth - prevDepth);
    this.CONF.house.previousHeight = (prevHeight + heightThoughDepth) * 1000;
    this.CONF.onUpdate();
  }

  selectWall(wall: Wall) {
    if (!this.state) return;
    const configWall = this.findWallByPosition(wall.position);
    this.APP.scene.highlightWall(configWall.id, this.getHouse());
    this.CONF.onUpdate();
  }

  deselectWalls() {
    this.APP.scene.resetWallHighlight();
    this.CONF.onUpdate();
  }

  selectBerging(berging: string) {
    if (!this.state) return;
    if (this.APP.scene.highlightBerging)
      this.APP.scene.highlightBerging(berging, this.getHouse());
    this.CONF.onUpdate();
  }

  deselectBerging(side?: "left" | "right") {
    if (this.APP.scene.resetBergingHighlight)
      this.APP.scene.resetBergingHighlight(side);
    this.CONF.onUpdate();
  }

  resetCamera() {
    // this.APP.scene.controls.target.copy({ x: 0, y: 0.5, z: 0 });
    // this.APP.scene.camera.position.copy({ x: 0, y: 4, z: 18 });
    // // this.APP.scene.camera.rotation.copy({x: 0, y: 0, z: 0});
    // this.APP.scene.camera.updateProjectionMatrix();
    if (window.APP) window.APP.resetCamera();
  }

  setRuler(type: "ruler" | "none") {
    if (!this.CONF.house.ruler) {
      this.CONF.house.ruler = { type: "none" };
    }
    this.CONF.house.ruler.type = type;
    this.CONF.onUpdate();
  }

  setDimensionsType(dimensionsType: "full" | "none") {
    if (!this.CONF.house.map2D) {
      this.CONF.house.map2D = { dimensionsType: "none" };
    }
    this.CONF.house.map2D.dimensionsType = dimensionsType;
    this.CONF.onUpdate(true);
  }
}

export default ConfiguratorService;
